import { agregarOrigen, quitarOrigen, reactivarOrigen, renombrarOrigen, type OrigenConfig } from '@barbones/shared';
import { Plus, RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/form';

interface Props {
  valor: OrigenConfig[];
  editable: boolean;
  guardando: boolean;
  onGuardar: (lista: OrigenConfig[]) => void;
}

/** Editor de «Cómo nos encontró»: agregar, renombrar y quitar (desactivar) opciones. */
export function EditorOrigenes({ valor, editable, guardando, onGuardar }: Props) {
  const [borrador, setBorrador] = useState(valor);
  const [nueva, setNueva] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setBorrador(valor), [valor]);

  const activas = borrador.filter((o) => o.activo);
  const quitadas = borrador.filter((o) => !o.activo);
  const etiquetasVacias = activas.some((o) => !o.etiqueta.trim());
  const modificado = JSON.stringify(borrador) !== JSON.stringify(valor);

  const aplicar = (cambio: () => OrigenConfig[]) => {
    try {
      setBorrador(cambio());
      setError(null);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo aplicar el cambio');
      return false;
    }
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <ul className="flex flex-col gap-1.5" aria-label="Opciones que se ofrecen">
        {activas.map((o) => (
          <li key={o.clave} className="flex items-center gap-2">
            <Input
              value={o.etiqueta}
              maxLength={60}
              disabled={!editable}
              onChange={(e) => setBorrador((l) => renombrarOrigen(l, o.clave, e.target.value))}
              aria-label={`Etiqueta de ${valor.find((v) => v.clave === o.clave)?.etiqueta ?? o.etiqueta}`}
              className="w-full sm:w-72"
            />
            {editable && (
              <Button
                variante="fantasma"
                tamano="icono"
                disabled={activas.length === 1}
                onClick={() => aplicar(() => quitarOrigen(borrador, o.clave))}
                aria-label={`Quitar ${o.etiqueta}`}
                title="Dejar de ofrecer"
              >
                <X />
              </Button>
            )}
          </li>
        ))}
      </ul>

      {editable && (
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (aplicar(() => agregarOrigen(borrador, nueva))) setNueva('');
          }}
        >
          <Input value={nueva} maxLength={60} onChange={(e) => setNueva(e.target.value)} aria-label="Nueva opción" placeholder="Nueva opción" className="w-full sm:w-72" />
          <Button type="submit" variante="secundario" tamano="sm" disabled={!nueva.trim()}>
            <Plus /> Agregar
          </Button>
        </form>
      )}
      {error && (
        <p role="alert" className="text-[12.5px] text-destructive">
          {error}
        </p>
      )}

      {quitadas.length > 0 && (
        <details className="text-[13px]">
          <summary className="cursor-pointer text-foreground-muted">Opciones quitadas ({quitadas.length})</summary>
          <ul className="mt-1.5 flex flex-col gap-1" aria-label="Opciones quitadas">
            {quitadas.map((o) => (
              <li key={o.clave} className="flex items-center gap-2">
                <span className="text-foreground-muted line-through">{o.etiqueta}</span>
                {editable && (
                  <Button variante="fantasma" tamano="sm" onClick={() => aplicar(() => reactivarOrigen(borrador, o.clave))} aria-label={`Volver a ofrecer ${o.etiqueta}`}>
                    <RotateCcw /> Volver a ofrecer
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {editable && (
        <div className="flex gap-2">
          <Button tamano="sm" cargando={guardando} disabled={!modificado || etiquetasVacias} onClick={() => onGuardar(borrador)}>
            Guardar cambios
          </Button>
          <Button
            variante="secundario"
            tamano="sm"
            disabled={!modificado}
            onClick={() => {
              setBorrador(valor);
              setError(null);
            }}
          >
            Descartar
          </Button>
        </div>
      )}
    </div>
  );
}
